const metadataColumn = (text, value, extra = {}) => ({
  text,
  value,
  sortable: false,
  group: "Publication",
  ...extra,
});

const metricColumn = (text, value, extra = {}) => ({
  text,
  value,
  sortable: false,
  kind: "number",
  align: "end",
  group: "Métriques",
  ...extra,
});

export const POST_BASIS_OPTIONS = [
  {
    value: "published",
    text: "Créées sur la période",
    description:
      "Publications publiées sur la période, avec leurs compteurs vie entière au dernier relevé.",
  },
  {
    value: "seen",
    text: "Vues sur la période",
    description:
      "Publications actives sur la période, quelle que soit leur date de sortie, avec leurs compteurs sommés jour par jour. Les ratios (CTR, taux, moyennes) restent vides.",
  },
];

export const NETWORK_LABELS = {
  bluesky: "Bluesky",
  facebook: "Facebook",
  instagram: "Instagram",
  linkedin: "LinkedIn",
  mastodon: "Mastodon",
  pinterest: "Pinterest",
  snapchat: "Snapchat",
  threads: "Threads",
  tiktok: "TikTok",
  twitter: "X",
  x: "X",
  youtube: "YouTube",
};

export const STATUS_LABELS = {
  200: "Publié",
  202: "En attente",
  503: "Erreur",
};

export const COMMON_EXPORT_LABELS = {
  id: "ID RMR",
  account_id: "ID compte RMR",
  account_source_id: "ID connexion source",
  external_account_id: "ID compte externe",
  source_account_id: "ID compte source",
  source_brand_id: "ID marque source",
  source_brand_name: "Marque",
  source_post_id: "ID publication Nonli",
  external_post_id: "ID publication externe",
  site_id: "ID site RMR",
  site_name: "Site",
  site_domain: "Domaine",
  created_at: "Créé dans RMR le",
  updated_at: "Mis à jour dans RMR le",
};

export const EXCEL_NUMERIC_FIELDS = new Set([
  "id",
  "account_id",
  "account_source_id",
  "site_id",
  "country_geoname_id",
  "state_geoname_id",
  "city_geoname_id",
  "status_code",
  "fan_count",
  "follower_count",
  "account_video_view_count",
  "followers_at_publication",
  "content_count",
  "amount",
  "monthly_projection",
]);

const accountColumns = [
  {
    text: "Marque",
    value: "brand",
    source: "source_brand_name",
    group: "Compte",
  },
  {
    text: "Domaine",
    value: "domain",
    source: "site_domain",
    sortable: false,
    group: "Compte",
  },
  {
    text: "Compte",
    value: "account_name",
    kind: "account",
    group: "Compte",
  },
  {
    text: "Réseau",
    value: "network",
    kind: "network",
    align: "center",
    group: "Compte",
  },
  {
    text: "Abonnés",
    value: "follower_count",
    kind: "number",
    align: "end",
    group: "Audience",
  },
  {
    text: "Fans",
    value: "fan_count",
    kind: "number",
    align: "end",
    group: "Audience",
  },
  {
    text: "Vues vidéo du compte",
    value: "account_video_view_count",
    kind: "number",
    align: "end",
    sortable: false,
    group: "Audience",
  },
  {
    text: "Vérification",
    value: "verification_status",
    kind: "verification",
    align: "center",
    sortable: false,
    group: "Compte",
  },
  {
    text: "Listening actif",
    value: "listening_activated",
    kind: "boolean",
    align: "center",
    sortable: false,
    group: "Source",
  },
  {
    text: "Connexion active",
    value: "is_active",
    kind: "boolean",
    align: "center",
    sortable: false,
    group: "Source",
  },
  {
    text: "Statut source",
    value: "source_status",
    sortable: false,
    group: "Source",
  },
  {
    text: "Type de compte",
    value: "account_subtype",
    sortable: false,
    group: "Compte",
  },
  { text: "Slug", value: "slug", sortable: false, group: "Compte" },
  {
    text: "Profil",
    value: "profile_url",
    kind: "link",
    sortable: false,
    group: "Compte",
  },
  {
    text: "Site du compte",
    value: "website_url",
    kind: "link",
    sortable: false,
    group: "Compte",
  },
  {
    text: "À propos",
    value: "about",
    kind: "longText",
    sortable: false,
    group: "Compte",
  },
  {
    text: "Catégorie",
    value: "category_text",
    sortable: false,
    group: "Compte",
  },
  {
    text: "Pays",
    value: "country_name",
    sortable: false,
    group: "Localisation",
  },
  {
    text: "Région",
    value: "state_name",
    sortable: false,
    group: "Localisation",
  },
  {
    text: "Ville",
    value: "city_name",
    sortable: false,
    group: "Localisation",
  },
];

const postMetadataColumns = [
  metadataColumn("Publication", "title", {
    kind: "post",
    source: "publication",
  }),
  metadataColumn("Marque", "brand", {
    source: "source_brand_name",
    sortable: true,
  }),
  metadataColumn("Date de publication", "published_at", {
    kind: "dateTime",
    sortable: true,
  }),
  metadataColumn("Réseau", "network", {
    kind: "network",
    align: "center",
    sortable: true,
  }),
  metadataColumn("Compte", "account_name", { kind: "account", sortable: true }),
  metadataColumn("Site", "site_name"),
  metadataColumn("Type", "post_type", { sortable: true }),
  metadataColumn("Statut", "status_code", { kind: "status", sortable: true }),
  metadataColumn("Auteur", "author_name"),
  metadataColumn("Message", "message", { kind: "longText" }),
  metadataColumn("Description", "description", { kind: "longText" }),
  metadataColumn("Lien", "post_url", { kind: "link" }),
  metadataColumn("URL cible", "target_url", { kind: "link" }),
  metadataColumn("URL courte", "short_url", { kind: "link" }),
  metadataColumn("Langue", "language_code"),
  metadataColumn("Tags", "tags", { kind: "list" }),
  metadataColumn("Topics", "topics", { kind: "list" }),
  metadataColumn("Tags personnalisés", "custom_tags", { kind: "list" }),
  metadataColumn("Abonnés à la publication", "followers_at_publication", {
    kind: "number",
    align: "end",
  }),
  metadataColumn("Compteurs mis à jour", "counters_updated_at", {
    kind: "dateTime",
  }),
  metadataColumn("Supprimée", "is_deleted", { kind: "boolean" }),
  metadataColumn("ID publication Nonli", "source_post_id"),
  metadataColumn("ID publication externe", "external_post_id"),
];

const postMetricColumns = [
  metricColumn("Clics", "click_count", { sortable: true }),
  metricColumn("Clics uniques", "unique_click_count"),
  metricColumn("CTR", "ctr", { kind: "percent", sortable: true }),
  metricColumn("Engagement", "engagement_count", { sortable: true }),
  metricColumn("Engagement/min", "engagement_per_minute", { decimals: 2 }),
  metricColumn("Engagement pondéré", "weighted_engagement", { decimals: 2 }),
  metricColumn("Engagement pondéré/min", "weighted_engagement_per_minute", {
    decimals: 2,
  }),
  metricColumn("Commentaires", "comment_count"),
  metricColumn("Réactions", "reaction_count"),
  metricColumn("Partages", "share_count"),
  metricColumn("Reach", "reach_count", { sortable: true }),
  metricColumn("Impressions", "impression_count", { sortable: true }),
  metricColumn("Vues vidéo", "video_view_count", { sortable: true }),
  metricColumn("Vues vidéo uniques", "video_view_unique_count"),
  metricColumn("Vues vidéo 3 s", "video_view_3s_count"),
  metricColumn("Lectures vidéo", "video_play_count"),
  metricColumn("Vues vidéo 60 s", "video_view_60s_count"),
  metricColumn("Durée moyenne vidéo (ms)", "video_avg_watch_time_ms"),
  metricColumn("Taux de complétion vidéo", "video_completion_rate", {
    kind: "percent",
  }),
  metricColumn("Taux de surperformance", "overperforming_rate", {
    kind: "percent",
  }),
  metricColumn("Reposts", "repost_count"),
  metricColumn("Citations", "quote_count"),
  metricColumn("Abonnés", "subscriber_count"),
  metricColumn("J’aime", "like_count"),
  metricColumn("Je n’aime pas", "dislike_count"),
  metricColumn("Enregistrements", "save_count"),
  metricColumn("Relectures", "replay_count"),
  metricColumn("Vues photo", "photo_view_count"),
  metricColumn("Vues photo uniques", "photo_view_unique_count"),
  metricColumn("Clics lien", "link_click_count"),
  metricColumn("Clics lien uniques", "link_click_unique_count"),
  metricColumn("Autres clics", "other_click_count"),
  metricColumn("Autres clics uniques", "other_click_unique_count"),
  metricColumn("Minutes regardées estimées", "estimated_minutes_watched"),
  metricColumn("Jours actifs (période)", "active_days"),
  metricColumn("Monétisation publication", "monetization_amount", {
    kind: "currency",
    decimals: 4,
    sortable: true,
  }),
  metricColumn(
    "Facebook · impressions organiques",
    "facebook_impression_organic_count"
  ),
  metricColumn(
    "Facebook · impressions organiques uniques",
    "facebook_impression_organic_unique_count"
  ),
  metricColumn(
    "Facebook · impressions payées",
    "facebook_impression_paid_count"
  ),
  metricColumn(
    "Facebook · impressions virales",
    "facebook_impression_viral_count"
  ),
  metricColumn(
    "Facebook · impressions virales uniques",
    "facebook_impression_viral_unique_count"
  ),
  metricColumn("Facebook · fans engagés", "facebook_engaged_fan_count"),
  metricColumn(
    "Facebook · utilisateurs engagés",
    "facebook_engaged_user_count"
  ),
  metricColumn(
    "Facebook · retours négatifs",
    "facebook_negative_feedback_count"
  ),
  metricColumn(
    "Facebook · vues vidéo organiques",
    "facebook_video_view_organic_count"
  ),
  metricColumn(
    "Facebook · vues vidéo organiques uniques",
    "facebook_video_view_organic_unique_count"
  ),
  metricColumn(
    "Facebook · vues vidéo payées",
    "facebook_video_view_paid_count"
  ),
  metricColumn(
    "Facebook · vues vidéo payées uniques",
    "facebook_video_view_paid_unique_count"
  ),
  metricColumn("Facebook · Love", "facebook_love_count"),
  metricColumn("Facebook · Wow", "facebook_wow_count"),
  metricColumn("Facebook · Haha", "facebook_haha_count"),
  metricColumn("Facebook · Triste", "facebook_sad_count"),
  metricColumn("Facebook · Colère", "facebook_angry_count"),
  metricColumn("Instagram · interactions", "instagram_total_interaction_count"),
  metricColumn("Instagram · durée Reel (ms)", "instagram_reel_watch_time_ms"),
  metricColumn(
    "Instagram · visites du profil",
    "instagram_profile_visit_count"
  ),
  metricColumn("Instagram · abonnements", "instagram_follow_count"),
  metricColumn(
    "Instagram · activité du profil",
    "instagram_profile_activity_count"
  ),
  metricColumn("LinkedIn · clics privés", "linkedin_private_click_count"),
  metricColumn("Snapchat · captures d’écran", "snapchat_screenshot_count"),
];

const revenueColumns = [
  { text: "Mois", value: "month", kind: "month", group: "Période" },
  {
    text: "Marque",
    value: "brand",
    source: "source_brand_name",
    group: "Compte",
  },
  { text: "Compte", value: "account_name", kind: "account", group: "Compte" },
  { text: "Réseau", value: "network", kind: "network", group: "Compte" },
  { text: "Site", value: "site", source: "site_name", group: "Compte" },
  {
    text: "Revenu constaté",
    value: "amount",
    kind: "currency",
    align: "end",
    group: "Revenus",
  },
  {
    text: "Atterrissage",
    value: "monthly_projection",
    kind: "currency",
    align: "end",
    sortable: false,
    group: "Revenus",
  },
  {
    text: "Publications",
    value: "content_count",
    kind: "number",
    align: "end",
    group: "Revenus",
  },
  { text: "Devise", value: "currency", sortable: false, group: "Revenus" },
  { text: "Source", value: "source", group: "Source" },
  {
    text: "Indicateur source",
    value: "source_metric",
    sortable: false,
    group: "Source",
  },
  {
    text: "Base de période",
    value: "period_basis",
    sortable: false,
    group: "Source",
  },
  {
    text: "Dernier contrôle",
    value: "last_checked_at",
    kind: "dateTime",
    group: "Dates",
  },
  {
    text: "Identifiant externe",
    value: "external_account_id",
    sortable: false,
    group: "Technique",
  },
  {
    text: "Identifiant marque source",
    value: "source_brand_id",
    sortable: false,
    group: "Technique",
  },
];

export const RESOURCE_CONFIG = {
  accounts: {
    endpoint: "accounts",
    title: "Comptes sociaux",
    description:
      "Comptes rattachés aux marques, informations de profil et audience disponible dans Nonli.",
    defaultSort: "site_priority",
    defaultSortDesc: true,
    columns: accountColumns,
    defaultColumns: [
      "brand",
      "domain",
      "account_name",
      "network",
      "follower_count",
      "fan_count",
      "account_video_view_count",
      "verification_status",
      "listening_activated",
      "is_active",
    ],
  },
  posts: {
    endpoint: "posts",
    title: "Publications et performances",
    description:
      "Publications et métriques récupérées quotidiennement depuis Nonli, créées ou vues sur la période selon le filtre.",
    defaultSort: "published_at",
    defaultSortDesc: true,
    columns: [...postMetadataColumns, ...postMetricColumns],
    defaultColumns: [
      "title",
      "brand",
      "published_at",
      "network",
      "account_name",
      "post_type",
      "author_name",
      "click_count",
      "impression_count",
      "reach_count",
      "engagement_count",
      "reaction_count",
      "comment_count",
      "share_count",
      "video_view_count",
      "ctr",
      "monetization_amount",
    ],
  },
  revenues: {
    endpoint: "revenues",
    title: "Revenus sociaux",
    description:
      "Revenus Facebook mensuels issus de Nonli. L’atterrissage est calculé à partir du mois précédent.",
    defaultSort: "month",
    defaultSortDesc: true,
    columns: revenueColumns,
    defaultColumns: revenueColumns.map((column) => column.value),
  },
};

// Les colonnes métriques doivent rester numériques dans le fichier XLSX afin
// que le métier puisse faire des sommes, moyennes et tableaux croisés.
postMetricColumns.forEach((column) => EXCEL_NUMERIC_FIELDS.add(column.value));
